import React, { useMemo } from 'react';
import { useRouter } from 'next/router';
import { getWindow, trackEvent } from '@surfline/web-common';
import { ScreenConfig } from 'types/contentCarousel';
import OverlayCard from '../OverlayCard';
import type { OverlayCardProps, OverlayCardData } from '../../types/cards';
import { seriesCardTransformer } from '../../utils/transformers';
import ContentCarousel from '../ContentCarousel';
import baseFetch, { createParamString } from '../../common/baseFetch';
import type { SeriesTaxonomy, SubCategory } from '../../types/editorial';
import config from '../../config';
import type { AppState } from '../../stores';
import getFeedGeotarget from '../../utils/getFeedGeotarget';

import type { TravelSeriesPlaylistType, TravelSeries } from '../../types/contentful/content-types';

/** Feed taxonomy term (may nest children like OpenSearch series hierarchy) */
type SeriesTaxonomyNode = Pick<SubCategory, 'slug' | 'taxonomy'> & {
  subCategories?: SeriesTaxonomyNode[];
};

/** Match feed-api taxonomy tree (nested subCategories) to a Contentful series slug */
const findSubCategoryBySlug = (
  subCategories: SeriesTaxonomyNode[] | undefined,
  slug: string | null | undefined,
): SeriesTaxonomyNode | undefined => {
  if (!subCategories?.length || !slug) return undefined;
  for (const sub of subCategories) {
    if (sub.slug === slug) return sub;
    const nested = findSubCategoryBySlug(sub.subCategories, slug);
    if (nested) return nested;
  }
  return undefined;
};

const seriesPlaylistCard = (
  itemData: OverlayCardData,
  cardProps: Omit<OverlayCardProps, 'data'>,
  router: { asPath?: string },
  screenConfig: ScreenConfig,
  carouselTitle?: string,
  index?: number,
  itemCtaTitle?: string,
  carouselRef?: React.RefObject<HTMLDivElement>,
) => (
  <OverlayCard
    {...cardProps}
    data={itemData}
    screenConfig={screenConfig}
    carouselRef={carouselRef}
    trackCardClick={() => {
      trackEvent('Carousel Clicked', {
        carouselItem: itemCtaTitle,
        carouselName: `landing page playlist ${carouselTitle}`,
        category: 'travel',
        path: router.asPath,
        positionNumber: index,
        referrer: `${getWindow()?.location?.href ?? ''}${router.asPath}`,
        search: getWindow().location.search,
        title: 'travel homepage',
        url: `${getWindow()?.location?.href ?? ''}${router.asPath}`,
      });
    }}
  />
);

type Props = {
  data: TravelSeriesPlaylistType;
  ssrReduxState: AppState;
};

const TravelSeriesPlaylist: React.FC<Props> = ({ data, ssrReduxState }) => {
  const { asPath } = useRouter();
  const geotarget = ssrReduxState ? getFeedGeotarget(ssrReduxState) || 'US' : 'US';
  const seriesTaxonomyEndpoint = useMemo(
    () =>
      `/feed/taxonomy/series/details?${createParamString({
        geotarget,
        limit: '-1',
        offset: '0',
      })}`,
    [geotarget],
  );

  const DEFAULT_TITLE = 'By Surfline Studios';
  const DEFAULT_SUBTITLE = '';
  const DEFAULT_COLORS = {
    bg: 'white',
    skeletonBg: '#eaeaea',
    skeletonItem: '#f7f7f7',
    headerText: '#171717',
    headerButton: '#171717',
  };

  const screenConfig = { small: 2, mobile: 1, medium: data?.playlistColumns || 3 };

  const seriesTransformer = (rawData: SeriesTaxonomy) => {
    const filteredData = data?.seriesCollection?.items
      ?.map((item: TravelSeries) => {
        const match = findSubCategoryBySlug(
          rawData?.subCategories as SeriesTaxonomyNode[] | undefined,
          item?.slug,
        );
        if (match) {
          return {
            featuredImage: {
              url: item?.featuredImage?.url,
              filename: item?.title,
            },
            caption: '',
            link: `${config.surflineHost}/${match?.taxonomy}/${match?.slug}`,
            subtitle: item?.subtitle,
            title: '',
            itemCtaTitle: item?.title,
          };
        }

        return null;
      })
      .filter(Boolean);

    return filteredData || [];
  };

  return (
    <ContentCarousel
      carouselItem={(itemData, index, carouselRef) =>
        seriesPlaylistCard(
          seriesCardTransformer(itemData),
          {
            noOverlay: true,
            showSubtitle: !!data?.showCardSubtitles,
            showCaption: !!data?.showCardCaptions,
          },
          { asPath },
          screenConfig,
          data?.title || DEFAULT_TITLE,
          index,
          itemData?.itemCtaTitle,
          carouselRef,
        )
      }
      dataTransform={(response) => seriesTransformer(response)}
      fetchConfig={{ endpoint: seriesTaxonomyEndpoint, fetch: baseFetch }}
      colors={data?.playlistColors || DEFAULT_COLORS}
      dataTest="series-playlist-section"
      itemAspectRatio={data?.itemAspectRatio || 1.1}
      numberOfitems={data?.playlistColumns || 3}
      screenConfig={screenConfig}
      showPageCount={!!data?.showPageCount}
      subtitle={data?.subtitle || DEFAULT_SUBTITLE}
      title={data?.title || DEFAULT_TITLE}
      useSLSectionPadding
    />
  );
};

export default TravelSeriesPlaylist;
